/**
 * Tab Cấu hình thông báo và Tài khoản gửi (`notifications.ts`, `sender-accounts.ts`, `workflow-refs.ts` của web-portal
 * native): kiểm biểu mẫu, quy tắc riêng/kế thừa khi xem theo một Project, lọc bảng, thân gửi máy chủ. Ràng buộc chép từ
 * `modules/rpa/backend/src/thong-bao/{quy-tac,kenh,tai-khoan-gui}.ts`; máy chủ vẫn là chốt cuối.
 *
 * Khác native: quy tắc lưu từng dòng (`/notification-rules`) thay vì ghi đè cả khối state; `event` là mã ổn định
 * (`run_failed`) thay nhãn tiếng Việt; phạm vi "Tất cả dự án" là `projectCode: null`.
 */
import type {
  KenhThongBao,
  LoaiTaiKhoanGui,
  MaHoaSmtp,
  ProjectOpenRpa,
  QuyTacThongBao,
  SuKienThongBao,
  TaiKhoanGui,
  ThanQuyTacThongBao,
  ThanTaiKhoanGui,
  WorkflowLoc,
} from '../types.ts';

/** Thứ tự trong ô chọn sự kiện; ba sự kiện kết quả lượt chạy lên đầu vì chỉ chúng có bộ gửi tự động. */
export const SU_KIEN: readonly SuKienThongBao[] = [
  'run_failed',
  'run_success',
  'all',
  'severity_critical',
  'severity_high',
  'severity_medium',
  'severity_low',
  'group_infra',
  'robot_offline',
];

/**
 * Sự kiện máy chủ thật sự phát tin: kết quả lượt chạy (`trangThaiCuaSuKien` của backend). Tin tổng hợp theo mức lỗi,
 * nhóm Hạ tầng và Robot offline lưu được nhưng chưa phát (ADR-0103 câu hỏi mở 15) — màn cảnh báo ở dòng đó.
 */
const SU_KIEN_DUOC_GUI: ReadonlySet<SuKienThongBao> = new Set(['run_failed', 'run_success', 'all']);

export const coBoGui = (event: SuKienThongBao): boolean => SU_KIEN_DUOC_GUI.has(event);

export const KENH: readonly KenhThongBao[] = ['telegram', 'email', 'zalo', 'whatsapp'];

/** Tên riêng của kênh — không dịch (`NHAN_KENH` của backend). */
export const TEN_KENH: Record<KenhThongBao, string> = {
  telegram: 'Telegram',
  email: 'Email (Gmail)',
  zalo: 'Zalo',
  whatsapp: 'WhatsApp',
};

/** Kênh dùng tài khoản gửi của tenant; Zalo, WhatsApp dùng tài khoản FoxAI đặt ở biến môi trường. */
export const LOAI_TAI_KHOAN_CUA_KENH: Record<KenhThongBao, LoaiTaiKhoanGui | null> = {
  telegram: 'telegram',
  email: 'gmail',
  zalo: null,
  whatsapp: null,
};

export const laKenhGui = (v: string): v is KenhThongBao => (KENH as readonly string[]).includes(v);

/** Kênh gửi được của quy tắc, theo thứ tự cố định; `teams`, `sms` (kênh cũ) không tính. */
export const kenhGuiDuoc = (q: Pick<QuyTacThongBao, 'channels'>): KenhThongBao[] => KENH.filter((k) => q.channels.includes(k));

export const kenhCu = (q: Pick<QuyTacThongBao, 'channels'>): string[] => q.channels.filter((k) => !laKenhGui(k));

export const GIOI_HAN_QUY_TAC = { ten: 200, dich: 500 } as const;
export const SO_EMAIL_TOI_DA = 20;
const MAU_EMAIL = /^[^\s@<>"',;]{1,64}@[^\s@<>"',;]{1,190}\.[A-Za-z]{2,}$/;
const CHAT_ID = /^-?\d{1,20}$|^@[A-Za-z][A-Za-z0-9_]{4,31}$/;
const ZALO_ID = /^\d{1,30}$/;
const SO_DIEN_THOAI = /^\+?[1-9]\d{7,14}$/;

/** "a@x.vn, b@y.vn" — dấu phẩy, chấm phẩy hoặc khoảng trắng. */
export function tachEmail(v: string): string[] {
  return v
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

/** Địa chỉ nhận đúng định dạng của kênh (`chuanDich` của backend). */
export function dichHopLe(kenh: KenhThongBao, v: string): boolean {
  const s = v.trim();
  switch (kenh) {
    case 'telegram':
      return CHAT_ID.test(s);
    case 'email': {
      const ds = tachEmail(s);
      return ds.length > 0 && ds.length <= SO_EMAIL_TOI_DA && ds.every((e) => MAU_EMAIL.test(e));
    }
    case 'zalo':
      return ZALO_ID.test(s);
    case 'whatsapp':
      return SO_DIEN_THOAI.test(s.replace(/[\s.-]/g, ''));
  }
}

// ─── Quy tắc ───

export interface FormQuyTac {
  name: string;
  event: SuKienThongBao;
  /** `''` = mọi Project. */
  projectCode: string;
  workflows: WorkflowLoc[];
  channels: KenhThongBao[];
  targets: Partial<Record<KenhThongBao, string>>;
  /** `''` = tài khoản mặc định của loại. */
  senders: Partial<Record<KenhThongBao, string>>;
  enabled: boolean;
}

export type LoiQuyTac = Partial<Record<'name' | 'channels' | KenhThongBao, 'batBuoc' | 'saiDinhDang'>>;

/** Form trống mặc định Telegram như native; đang xem một Project thì quy tắc mới thuộc Project đó. */
export function formQuyTac(q: QuyTacThongBao | null, projectMacDinh = ''): FormQuyTac {
  if (!q) {
    return {
      name: '',
      event: 'run_failed',
      projectCode: projectMacDinh,
      workflows: [],
      channels: ['telegram'],
      targets: {},
      senders: {},
      enabled: true,
    };
  }
  const targets: FormQuyTac['targets'] = {};
  const senders: FormQuyTac['senders'] = {};
  for (const k of KENH) {
    targets[k] = q.targets[k] ?? '';
    if (q.senders[k]) senders[k] = q.senders[k];
  }
  return {
    name: q.name,
    event: q.event,
    projectCode: q.projectCode ?? '',
    workflows: q.workflows,
    channels: kenhGuiDuoc(q),
    targets,
    senders,
    enabled: q.enabled,
  };
}

export function kiemQuyTac(f: FormQuyTac): LoiQuyTac {
  const loi: LoiQuyTac = {};
  if (!f.name.trim()) loi.name = 'batBuoc';
  if (f.channels.length === 0) loi.channels = 'batBuoc';
  for (const k of f.channels) {
    const v = (f.targets[k] ?? '').trim();
    if (!v) loi[k] = 'batBuoc';
    else if (!dichHopLe(k, v)) loi[k] = 'saiDinhDang';
  }
  return loi;
}

/**
 * Thân gửi máy chủ. Chỉ giữ người nhận và tài khoản gửi của kênh đã chọn; kênh cũ (`teams`, `sms`) của quy tắc đang sửa
 * được giữ lại — form không hiện chúng nhưng không được làm mất.
 */
export function thanQuyTac(f: FormQuyTac, goc: QuyTacThongBao | null): ThanQuyTacThongBao {
  const targets: Record<string, string> = {};
  const senders: Record<string, string> = {};
  for (const k of f.channels) {
    targets[k] = (f.targets[k] ?? '').trim();
    const tk = f.senders[k];
    if (tk && LOAI_TAI_KHOAN_CUA_KENH[k]) senders[k] = tk;
  }
  const cu = goc ? kenhCu(goc) : [];
  for (const k of cu) if (goc?.targets[k]) targets[k] = goc.targets[k];
  return {
    name: f.name.trim(),
    event: f.event,
    projectCode: f.projectCode || null,
    workflows: f.workflows,
    channels: [...f.channels, ...cu],
    targets,
    senders,
    enabled: f.enabled,
  };
}

const cungProject = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

/** Quy tắc riêng của Project (không tính quy tắc chung kế thừa). */
export const laCuaProject = (q: Pick<QuyTacThongBao, 'projectCode'>, project: string): boolean =>
  q.projectCode !== null && cungProject(q.projectCode, project);

/** Quy tắc chung đang bị loại ở Project này. */
export const biLoaiTru = (q: Pick<QuyTacThongBao, 'projectCode' | 'excludedProjects'>, project: string): boolean =>
  q.projectCode === null && q.excludedProjects.some((p) => cungProject(p, project));

/** Quy tắc áp dụng được cho Project: của riêng nó và mọi quy tắc chung (kể cả đang bị loại, để bật lại được). */
export const quyTacCuaProject = (ds: readonly QuyTacThongBao[], project: string): QuyTacThongBao[] =>
  ds.filter((q) => q.projectCode === null || laCuaProject(q, project));

/** Dòng kế thừa: đang xem một Project và quy tắc là quy tắc chung. */
export const laKeThua = (q: Pick<QuyTacThongBao, 'projectCode'>, project: string): boolean => Boolean(project) && q.projectCode === null;

/** Quy tắc có đang bật thật sự ở chế độ xem hiện tại (dòng kế thừa còn phải không bị loại trừ). */
export const dangBat = (q: QuyTacThongBao, project: string): boolean => (laKeThua(q, project) ? q.enabled && !biLoaiTru(q, project) : q.enabled);

export interface BoLocQuyTac {
  tim: string;
  event: string;
  kenh: string;
  /** `bat` | `tat` | rỗng. */
  trangThai: string;
}

export function locQuyTac(ds: readonly QuyTacThongBao[], loc: BoLocQuyTac, project: string): QuyTacThongBao[] {
  const tim = loc.tim.trim().toLowerCase();
  return ds.filter(
    (q) =>
      (!loc.event || q.event === loc.event) &&
      (!loc.kenh || q.channels.includes(loc.kenh)) &&
      (!loc.trangThai || (loc.trangThai === 'bat') === dangBat(q, project)) &&
      (!tim || [q.name, q.id, q.projectCode ?? ''].some((v) => v.toLowerCase().includes(tim))),
  );
}

// ─── Workflow lọc (ô chọn Workflow của quy tắc và tài khoản gửi) ───

export interface NhomWorkflow {
  /** Tên Project; `''` cho nhóm Workflow đã lưu mà OpenCore không còn. */
  label: string;
  options: { value: string; label: string }[];
}

/**
 * Lựa chọn nhóm theo Project trong phạm vi (`projectId` rỗng = mọi Project). Workflow đã lưu mà OpenCore không còn vẫn
 * hiện (theo tên đã lưu) để người dùng thấy mà bỏ đi.
 */
export function nhomWorkflow(projects: readonly ProjectOpenRpa[], projectId: string, daChon: readonly WorkflowLoc[]): NhomWorkflow[] {
  const nhom = projects
    .filter((p) => !projectId || cungProject(p.id, projectId))
    .filter((p) => p.workflows.length > 0)
    .map((p) => ({ label: p.name, options: p.workflows.map((w) => ({ value: w.id, label: w.name })) }));
  const biet = new Set(nhom.flatMap((g) => g.options.map((o) => o.value)));
  const cu = daChon.filter((w) => !biet.has(w.id || w.name)).map((w) => ({ value: w.id || w.name, label: w.name || w.id }));
  return cu.length ? [...nhom, { label: '', options: cu }] : nhom;
}

/** Id chọn trong ô → `{ id, name }` để lưu; tên lấy từ OpenCore, không thấy thì giữ tên đã lưu. */
export function chonWorkflow(ids: readonly string[], projects: readonly ProjectOpenRpa[], truoc: readonly WorkflowLoc[]): WorkflowLoc[] {
  const tatCa = projects.flatMap((p) => p.workflows);
  return ids.map((id) => {
    const w = tatCa.find((x) => x.id === id);
    if (w) return { id: w.id, name: w.name };
    return truoc.find((x) => (x.id || x.name) === id) ?? { id, name: id };
  });
}

/** Đổi phạm vi sang một Project thì bỏ Workflow không thuộc Project đó (Workflow đã mất khỏi OpenCore vẫn giữ). */
export function giuWorkflowTrongPhamVi(ds: readonly WorkflowLoc[], projects: readonly ProjectOpenRpa[], projectId: string): WorkflowLoc[] {
  if (!projectId) return [...ds];
  const tatCa = new Set(projects.flatMap((p) => p.workflows.map((w) => w.id)));
  const trong = new Set(projects.filter((p) => cungProject(p.id, projectId)).flatMap((p) => p.workflows.map((w) => w.id)));
  return ds.filter((w) => trong.has(w.id) || !tatCa.has(w.id));
}

// ─── Tài khoản gửi ───

export const LOAI_TAI_KHOAN: readonly LoaiTaiKhoanGui[] = ['gmail', 'telegram'];
export const MA_HOA: readonly MaHoaSmtp[] = ['starttls', 'ssl', 'none'];
export const SMTP_MAC_DINH = { smtpHost: 'smtp.gmail.com', smtpPort: 587, encryption: 'starttls' as MaHoaSmtp };
export const GIOI_HAN_TAI_KHOAN = { ten: 120, host: 253, matKhau: 200, token: 200 } as const;
const HOST = /^[A-Za-z0-9.-]{1,253}$/;
const BOT_TOKEN = /^\d{5,15}:[A-Za-z0-9_-]{30,}$/;

export interface FormTaiKhoan {
  type: LoaiTaiKhoanGui;
  name: string;
  email: string;
  smtpHost: string;
  smtpPort: string;
  encryption: MaHoaSmtp;
  /** Gmail: mật khẩu ứng dụng; Telegram: Bot token. Trống khi sửa = giữ bí mật đã lưu. */
  biMat: string;
  workflows: WorkflowLoc[];
  isDefault: boolean;
  enabled: boolean;
}

export type LoiTaiKhoan = Partial<
  Record<'name' | 'email' | 'smtpHost' | 'smtpPort' | 'biMat' | 'isDefault', 'batBuoc' | 'saiDinhDang' | 'macDinhPhaiBat'>
>;

export function formTaiKhoan(a: TaiKhoanGui | null, loai: LoaiTaiKhoanGui = 'gmail'): FormTaiKhoan {
  return {
    type: a?.type ?? loai,
    name: a?.name ?? '',
    email: a?.email ?? '',
    smtpHost: a?.smtpHost ?? SMTP_MAC_DINH.smtpHost,
    smtpPort: String(a?.smtpPort ?? SMTP_MAC_DINH.smtpPort),
    encryption: a?.encryption ?? SMTP_MAC_DINH.encryption,
    biMat: '',
    workflows: a?.workflows ?? [],
    isDefault: a?.isDefault ?? false,
    enabled: a?.enabled ?? true,
  };
}

/** Kiểm như `parseTaiKhoan` của backend; `dangSua`: bí mật trống là giữ cái đã lưu. */
export function kiemTaiKhoan(f: FormTaiKhoan, dangSua: boolean): LoiTaiKhoan {
  const loi: LoiTaiKhoan = {};
  if (!f.name.trim()) loi.name = 'batBuoc';
  const biMat = f.type === 'gmail' ? f.biMat.replace(/\s+/g, '') : f.biMat.trim();
  if (!biMat && !dangSua) loi.biMat = 'batBuoc';
  if (f.type === 'gmail') {
    const email = f.email.trim();
    if (!email) loi.email = 'batBuoc';
    else if (!MAU_EMAIL.test(email)) loi.email = 'saiDinhDang';
    if (f.smtpHost.trim() && !HOST.test(f.smtpHost.trim())) loi.smtpHost = 'saiDinhDang';
    const port = Number(f.smtpPort);
    if (!Number.isInteger(port) || port < 1 || port > 65535) loi.smtpPort = 'saiDinhDang';
  } else if (biMat && !BOT_TOKEN.test(biMat)) {
    loi.biMat = 'saiDinhDang';
  }
  if (f.isDefault && !f.enabled) loi.isDefault = 'macDinhPhaiBat';
  return loi;
}

/** Thân gửi máy chủ; bí mật trống không gửi (máy chủ giữ cái đã lưu). App Password Google bỏ khoảng trắng. */
export function thanTaiKhoan(f: FormTaiKhoan): ThanTaiKhoanGui {
  const chung = { type: f.type, name: f.name.trim(), workflows: f.workflows, isDefault: f.isDefault, enabled: f.enabled };
  if (f.type === 'telegram') {
    const botToken = f.biMat.trim();
    return botToken ? { ...chung, botToken } : chung;
  }
  const appPassword = f.biMat.replace(/\s+/g, '');
  return {
    ...chung,
    email: f.email.trim().toLowerCase(),
    smtpHost: f.smtpHost.trim() || SMTP_MAC_DINH.smtpHost,
    smtpPort: Number(f.smtpPort),
    encryption: f.encryption,
    ...(appPassword ? { appPassword } : {}),
  };
}

/** Bật/tắt và đặt mặc định ngay trên bảng: gửi lại toàn bộ thông tin, không kèm bí mật (máy chủ giữ cái đã lưu). */
export function thanDoiNhanh(a: TaiKhoanGui, doi: { enabled?: boolean; isDefault?: boolean }): ThanTaiKhoanGui {
  const than = thanTaiKhoan({ ...formTaiKhoan(a), ...doi });
  // Tắt tài khoản mặc định thì bỏ mặc định luôn — máy chủ không nhận "mặc định mà tắt".
  return doi.enabled === false ? { ...than, isDefault: false } : than;
}

/** Tài khoản bật của một loại, mặc định lên đầu — để chọn trong quy tắc thông báo. */
export function taiKhoanCuaLoai(ds: readonly TaiKhoanGui[], loai: LoaiTaiKhoanGui): TaiKhoanGui[] {
  return ds.filter((a) => a.type === loai && a.enabled).sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

export interface BoLocTaiKhoan {
  tim: string;
  loai: string;
  /** `bat` | `tat` | rỗng. */
  trangThai: string;
}

export function locTaiKhoan(ds: readonly TaiKhoanGui[], loc: BoLocTaiKhoan): TaiKhoanGui[] {
  const tim = loc.tim.trim().toLowerCase();
  return ds.filter(
    (a) =>
      (!loc.loai || a.type === loc.loai) &&
      (!loc.trangThai || (loc.trangThai === 'bat') === a.enabled) &&
      (!tim || [a.name, a.email ?? '', a.id].some((v) => v.toLowerCase().includes(tim))),
  );
}
